/**
 * Step 5 (Assign) helpers — pure, testable logic for the assignment grid's
 * working draft, applying a server auto-assign proposal, the coaching-workload
 * rollup, computing which games changed (for save), and CSV export.
 *
 * The grid holds a client-side DRAFT (gameId → coach user ids) seeded from each
 * game's current explicit assignees. Auto-Assign overwrites the draft with the
 * server's proposal; the coach then tweaks and saves. Nothing here calls the
 * network — the component owns loading/saving.
 */

import type { OrganizationMember } from '../../../../../types/api';
import type {
  AutoAssignment,
  TournamentGameDto,
} from '../../../../../types/tournament-games';
import { formatEventClockTime } from '../../../../../utils/calendarTime';

/** gameId → the coach user ids assigned to that game in the working draft. */
export type AssignmentDraft = Record<string, string[]>;

/** Seed a draft from each game's current explicit assignees (its own coaches). */
export function seedDraft(games: readonly TournamentGameDto[]): AssignmentDraft {
  const draft: AssignmentDraft = {};
  for (const game of games) {
    draft[game.id] = game.assignees.map((a) => a.userId);
  }
  return draft;
}

/**
 * Apply an auto-assign proposal over the draft: each proposed game gets exactly
 * its one coach and every unassignable game is cleared. When `scopeGameIds` is
 * given (the games on screen), proposal entries for any other game are ignored,
 * so games outside the scope keep their draft even if an older API planned them.
 * Returns a new object; the input is not mutated.
 */
export function applyProposal(
  draft: AssignmentDraft,
  assignments: readonly AutoAssignment[],
  unassignableGameIds: readonly string[],
  scopeGameIds?: readonly string[],
): AssignmentDraft {
  const scope = scopeGameIds ? new Set(scopeGameIds) : null;
  const inScope = (gameId: string) => !scope || scope.has(gameId);
  const next: AssignmentDraft = { ...draft };
  for (const gameId of unassignableGameIds) {
    if (inScope(gameId)) next[gameId] = [];
  }
  for (const { gameId, coachUserId } of assignments) {
    if (inScope(gameId)) next[gameId] = [coachUserId];
  }
  return next;
}

/**
 * The unsaved draft for games OUTSIDE the scope being planned, sent as `draftAssignees` so the
 * server counts a coach's off-screen games (including unsaved grid changes) and never
 * double-books them. Only games whose draft differs from the grid's `saved` baseline are sent
 * (falling back to the game's loaded own assignees when the baseline has no entry): the server
 * reads an absent game as "use what is saved", so the request stays small however many games the
 * tournament has. The baseline, not `game.assignees`, is the reference because a partial Save &
 * Finish (assignees saved, publish failed) moves the baseline without refetching the games.
 * Game-level coaches only; tournament-level coaches are server-known.
 */
export function offScopeDraft(
  games: readonly TournamentGameDto[],
  draft: AssignmentDraft,
  scopeGameIds: readonly string[],
  savedBaseline: AssignmentDraft = {},
): AssignmentDraft {
  const scope = new Set(scopeGameIds);
  const out: AssignmentDraft = {};
  for (const game of games) {
    if (scope.has(game.id)) continue;
    const current = draft[game.id] ?? [];
    const saved = savedBaseline[game.id] ?? game.assignees.map((a) => a.userId);
    if (!sameIdSet(current, saved)) out[game.id] = [...current];
  }
  return out;
}

/** A coach assigned to the whole tournament (covers every game by design). */
export type TournamentCoach = { userId: string; displayName: string; initials: string };

/**
 * Coaches assigned to the whole tournament. A game's `effectiveCoaches` is the tournament's
 * coaches plus the game's own, so any effective coach who is not among that game's own assignees
 * is a tournament coach (the same rule as mobile). `extraIds` (from an auto-assign response) are
 * added, named from `members` when no game carries them.
 */
export function tournamentCoaches(
  games: readonly TournamentGameDto[],
  extraIds: readonly string[] = [],
  members: readonly OrganizationMember[] = [],
): TournamentCoach[] {
  const byId = new Map<string, TournamentCoach>();
  for (const game of games) {
    const own = new Set(game.assignees.map((a) => a.userId));
    for (const c of game.effectiveCoaches ?? []) {
      if (!own.has(c.userId) && !byId.has(c.userId)) byId.set(c.userId, { ...c });
    }
  }
  const memberById = new Map(members.map((m) => [m.id, m]));
  for (const id of extraIds) {
    if (byId.has(id)) continue;
    const m = memberById.get(id);
    byId.set(id, {
      userId: id,
      displayName: m?.displayName ?? 'Coach',
      initials: m?.initials ?? '?',
    });
  }
  return [...byId.values()];
}

/** The planner's assumed game length (AutoAssignPlanner.AssumedGameDuration). */
export const ASSUMED_GAME_MS = 2 * 60 * 60 * 1000;

type TimedGame = Pick<TournamentGameDto, 'id' | 'startsAtUtc' | 'timeZone'>;

/** "2026-07-05 11:00" in the game's own zone, for the same-clock-time check. */
function localClockKey(game: TimedGame): string {
  const start = new Date(game.startsAtUtc);
  if (Number.isNaN(start.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: game.timeZone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(start);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
  } catch {
    return start.toISOString().slice(0, 16);
  }
}

/**
 * Two games clash for one coach: their assumed two hour windows overlap by UTC start (half-open,
 * so back-to-back is fine, matching the planner), or they share the same local date and clock
 * time in different zones (a mis-zoned game still reads as a double booking to the coach).
 */
export function gamesOverlap(a: TimedGame, b: TimedGame): boolean {
  if (a.id === b.id) return false;
  const sa = Date.parse(a.startsAtUtc);
  const sb = Date.parse(b.startsAtUtc);
  if (!Number.isNaN(sa) && !Number.isNaN(sb) && Math.abs(sa - sb) < ASSUMED_GAME_MS) return true;
  if (a.timeZone !== b.timeZone) {
    const ka = localClockKey(a);
    return ka !== '' && ka === localClockKey(b);
  }
  return false;
}

/** One coach on this game who also holds an overlapping game. */
export type CoachConflict = { coachUserId: string; otherGameId: string };

/**
 * Every game where a game-level coach also holds an overlapping game, across ALL the given games
 * (not only those on screen). Tournament-level coaches are on every game by design and are never
 * flagged. Returns gameId -> conflicts (one per coach, the earliest clashing game).
 */
export function findCoachConflicts(
  games: readonly TimedGame[],
  draft: AssignmentDraft,
  ignoreCoachIds: Iterable<string> = [],
): Map<string, CoachConflict[]> {
  const ignore = new Set(ignoreCoachIds);
  const sorted = [...games].sort(
    (a, b) => Date.parse(a.startsAtUtc) - Date.parse(b.startsAtUtc),
  );
  const out = new Map<string, CoachConflict[]>();
  for (const game of sorted) {
    const coaches = new Set(draft[game.id] ?? []);
    for (const coach of coaches) {
      if (ignore.has(coach)) continue;
      const other = sorted.find(
        (g) => g.id !== game.id && (draft[g.id] ?? []).includes(coach) && gamesOverlap(game, g),
      );
      if (!other) continue;
      const list = out.get(game.id) ?? [];
      list.push({ coachUserId: coach, otherGameId: other.id });
      out.set(game.id, list);
    }
  }
  return out;
}

/**
 * Add `userId` to each of `targetGames` (the games on screen) unless they are already on it or
 * it would overlap a game they already hold anywhere in `allGames`. Games are taken in start
 * order so the earlier of two clashing games wins. Returns the new draft and how many were
 * added or skipped.
 */
export function assignGamesToCoach(
  draft: AssignmentDraft,
  allGames: readonly TimedGame[],
  targetGames: readonly TimedGame[],
  userId: string,
): { draft: AssignmentDraft; assigned: number; skipped: number } {
  let next = draft;
  let assigned = 0;
  let skipped = 0;
  const ordered = [...targetGames].sort(
    (a, b) => Date.parse(a.startsAtUtc) - Date.parse(b.startsAtUtc),
  );
  for (const game of ordered) {
    const current = next[game.id] ?? [];
    if (current.includes(userId)) continue;
    const clash = allGames.some(
      (g) => g.id !== game.id && (next[g.id] ?? []).includes(userId) && gamesOverlap(game, g),
    );
    if (clash) {
      skipped += 1;
      continue;
    }
    next = setGameCoaches(next, game.id, [...current, userId]);
    assigned += 1;
  }
  return { draft: next, assigned, skipped };
}

/** Replace one game's coaches in the draft (immutably). */
export function setGameCoaches(
  draft: AssignmentDraft,
  gameId: string,
  coachUserIds: string[],
): AssignmentDraft {
  return { ...draft, [gameId]: coachUserIds };
}

/** How many games have at least one coach assigned in the draft. */
export function coveredGameCount(draft: AssignmentDraft): number {
  return Object.values(draft).filter((ids) => ids.length > 0).length;
}

export type WorkloadRow = { member: OrganizationMember; gameCount: number };

/**
 * Per-coach game count from the draft, one row per org member (including those
 * with zero games). Sorted by busiest first, then display name for stability.
 */
export function computeWorkload(
  draft: AssignmentDraft,
  members: readonly OrganizationMember[],
): WorkloadRow[] {
  const counts: Record<string, number> = {};
  for (const ids of Object.values(draft)) {
    for (const id of ids) {
      counts[id] = (counts[id] ?? 0) + 1;
    }
  }
  return members
    .map((member) => ({ member, gameCount: counts[member.id] ?? 0 }))
    .sort(
      (a, b) =>
        b.gameCount - a.gameCount ||
        a.member.displayName.localeCompare(b.member.displayName),
    );
}

/** Two id lists hold the same set (order/duplication-insensitive). */
function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((id) => seen.has(id));
}

/**
 * Game ids whose draft coaches differ from the saved baseline — the exact set of
 * per-game PUTs needed to persist the draft (each saved independently).
 */
export function changedGameIds(
  saved: AssignmentDraft,
  draft: AssignmentDraft,
): string[] {
  const ids = new Set([...Object.keys(saved), ...Object.keys(draft)]);
  const changed: string[] = [];
  for (const id of ids) {
    if (!sameIdSet(saved[id] ?? [], draft[id] ?? [])) {
      changed.push(id);
    }
  }
  return changed;
}

/** Quote a CSV field iff it contains a comma, quote, or newline (RFC 4180). */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * CSV of the assignment plan — one row per game, coaches taken from the DRAFT
 * (what you're about to save, not the server's current coverage). Times render
 * in each game's own timezone. Games are emitted in the order given.
 */
export function assignmentsToCsv(
  games: readonly TournamentGameDto[],
  draft: AssignmentDraft,
  members: readonly OrganizationMember[],
): string {
  const nameById = new Map(members.map((m) => [m.id, m.displayName]));
  const header = ['Date', 'Time', 'Field', 'Home', 'Away', 'Assigned coaches'];
  const rows = games.map((game) => {
    const date = new Date(game.startsAtUtc).toISOString().slice(0, 10);
    const coaches = (draft[game.id] ?? [])
      .map((id) => nameById.get(id) ?? 'Unknown')
      .join('; ');
    return [
      date,
      formatEventClockTime({ startsAtUtc: game.startsAtUtc, timeZone: game.timeZone }),
      game.fieldName,
      game.homeTeamNameRaw,
      game.awayTeamNameRaw,
      coaches,
    ].map(csvCell);
  });
  return [header.map(csvCell), ...rows].map((r) => r.join(',')).join('\n');
}
